import type { Omittable } from "@salestrak/ui";

import { StartFollowUpDialog } from "#app/components/follow-ups/start-follow-up-dialog";
import { useProjectCycleMutations } from "#app/hooks/issue-view/use-project-cycle-mutations";
import {
  DEFAULT_FOLLOW_UP_CYCLE,
  effectiveStageCycle,
  nearestFollowUpCyclePreset,
  type ProjectAge,
  type StagePin,
  type WorkflowCycle,
} from "#app/lib/project-updates";

/**
 * The Start Follow up dialog over a Project whose updates are off — opened by
 * the detail header's verb, the Follow up section's action row and the board
 * card's ghost pulse. The family dialog owns its one sentence — what
 * starting does, the same on every target — never that updates are off:
 * the title is the act, and a dialog that opens by announcing a disabled
 * state reads as a warning. Picking a cycle writes the Project's own, which
 * is what turns updates back on server-side. Pre-answers with the rung below when it
 * carries a cycle (a Project opted out under a running Stage) — snapped to
 * the ladder, since a Workflow's "Every 30 days" is not a rung — else the
 * family default.
 */
export function ProjectStartFollowUpDialog({
  issueId,
  stage,
  workflow,
  open,
  onOpenChange,
  via,
}: Omittable<{
  issueId: string;
  age?: ProjectAge | null;
  stage?: StagePin | null;
  workflow?: WorkflowCycle | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The surface that opened the dialog, for `follow_up.start` analytics:
   *  "detail_header", "follow_up_section" or "card_ghost". */
  via: string;
}>) {
  const { saveCycle } = useProjectCycleMutations(issueId);
  const inherited = effectiveStageCycle(stage, workflow).cycle;
  const defaultCycle = inherited ? nearestFollowUpCyclePreset(inherited) : DEFAULT_FOLLOW_UP_CYCLE;
  return (
    <StartFollowUpDialog
      target="project"
      open={open}
      onOpenChange={onOpenChange}
      defaultCycle={defaultCycle}
      onStart={({ cycle }) => {
        void saveCycle(cycle, {
          started: true,
          via,
          cycleIsDefault:
            cycle.amount === defaultCycle.amount && cycle.step === defaultCycle.step,
        });
      }}
    />
  );
}
